import React, { useEffect, useRef } from 'react';
import idleStrip from '../../assets/tars/idle-wave-strip.png';
import tumbleStrip from '../../assets/tars/typing-tumble-strip.png';

const IDLE_FRAMES = 4;
const IDLE_INTERVAL_MS = 700;
const TUMBLE_FRAMES = 15;
const TUMBLE_INTERVAL_MS = 75;

interface TarsAvatarProps {
  size: number;
  isTyping?: boolean;
  className?: string;
}

// Frame-stepping is driven by JS writing `background-position` directly rather
// than CSS keyframes: percentage-based keyframe stops still interpolate across
// the gap between frames, which reads as jitter on a heavily-downscaled sprite.
const TarsAvatar: React.FC<TarsAvatarProps> = ({ size, isTyping = false, className = '' }) => {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    el.style.backgroundPosition = '0 0';

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const frameCount = isTyping ? TUMBLE_FRAMES : IDLE_FRAMES;
    const intervalMs = isTyping ? TUMBLE_INTERVAL_MS : IDLE_INTERVAL_MS;
    let frame = 0;
    const timer = setInterval(() => {
      frame = (frame + 1) % frameCount;
      el.style.backgroundPosition = `-${frame * size}px 0`;
    }, intervalMs);

    return () => clearInterval(timer);
  }, [isTyping, size]);

  const frameCount = isTyping ? TUMBLE_FRAMES : IDLE_FRAMES;

  return (
    <span
      ref={ref}
      aria-hidden="true"
      className={`block shrink-0 ${className}`}
      style={{
        width: size,
        height: size,
        backgroundImage: `url(${isTyping ? tumbleStrip : idleStrip})`,
        backgroundSize: `${size * frameCount}px ${size}px`,
        backgroundPosition: '0 0',
        backgroundRepeat: 'no-repeat',
      }}
    />
  );
};

export default TarsAvatar;
